import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { Button } from '@mui/material';
import { Header } from '../../components/Header';
import { EVENTS } from './calendarConstants';
import { PLAN_START, PLAN_END, TRACKS, TRACK_RUNS } from './watchPlan';
import { buildDinnerRuns } from './dinnerRotation';
import {
    ACTIVITY_KINDS,
    RECURRENCES,
    describeRecurrence,
    expandActivityEvents,
    isBakingActivity,
    isRecurring,
    kindForSymbol,
    monthlyPattern,
} from './activityEvents';
import { buildMovieRuns } from './movieRotation';
import { buildNalasMenuRuns, recipesOfType } from './nalasMenuRotation';
import { buildRestaurantRuns } from './restaurantRotation';
import { buildNewShowRuns } from './newShowRotation';
import { expandTraditions } from './traditions';
import {
    getDinners,
    getMovies,
    getNalasMenu,
    getRestaurants,
    getShows,
    recommendWatch,
} from './dinnersServer';
import {
    addActivityEvent,
    deleteActivityEvent,
    getActivityEvents,
} from './activityEventsServer';
import { getActivities } from '../bump/server';

import './calendar.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

const toIsoDate = (year, month, day) =>
    `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

// Parse as local time (bare ISO dates are otherwise treated as UTC and can
// render as the previous day).
const formatIsoDate = (iso) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });

// For a span that's already sitting under its own full date heading, where two
// more spelled-out dates would be longer than the tradition they describe.
const formatShortDate = (iso) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
    });

// "14:30" (the time input's value format) -> "2:30 PM".
const formatTime = (time) =>
    new Date(`1970-01-01T${time}`).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
    });

// Every track runs in parallel, so no day belongs to one track — on any date each
// track is simply partway through a show. With every show on for one Sunday-to-
// Saturday week, a show belongs to a week row rather than a day, so the grid
// gives each week a band naming its head, heart and body shows and leaves the
// day lanes to traditions and things you scheduled.
const SHOW_RUNS = Object.entries(TRACK_RUNS).flatMap(([code, runs]) =>
    runs.map((show, i) => ({ id: `run-${code}-${i}`, code, show, index: i })));

// The show tracks, in TRACKS order: everything the grid isn't silent about.
const SHOW_TRACKS = Object.keys(TRACKS).filter((code) => !TRACKS[code].gridSilent);

// What each show track has on for a week row. A row can straddle a switch (the
// long runs before the weekly rotation, or a month's padded edges), so the show
// on the row's last real day is the one it's named for.
const weekShowsFor = (week, runs) => {
    const days = week.filter(Boolean);
    if (!days.length) return [];
    const last = days[days.length - 1].iso;
    return SHOW_TRACKS
        .map((code) => {
            const run = runs.find(
                (r) => r.code === code && r.show.start <= last && last <= r.show.end);
            return run ? { code, run, first: days[0].iso } : null;
        })
        .filter(Boolean);
};

// What every track is on for a given date. Track order follows TRACKS, so a new
// track shows up simply by being declared there and supplying runs.
const slateFor = (iso, runs) => Object.keys(TRACKS)
    .map((code) => {
        const run = runs.find(
            (r) => r.code === code && r.show.start <= iso && iso <= r.show.end);
        return run ? { code, run } : null;
    })
    .filter(Boolean);

// A split show's title carries its season range, e.g. "Friends (S3–4)". In a day
// cell that suffix is the first thing ellipsis eats, so the grid shows the name
// and the season marker as separate pieces and only truncates the name.
const splitTitle = (show) => {
    const match = show.series && show.title.match(/^(.*) \(([^)]+)\)$/);
    return match ? { name: match[1], part: match[2] } : { name: show.title, part: null };
};

// Weeks of the month: arrays of 7 cells, each { day, iso } or null padding
// so the grid always starts on Sunday.
const buildWeeks = (year, month) => {
    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = Array(firstWeekday).fill(null);
    for (let day = 1; day <= daysInMonth; day++) {
        cells.push({ day, iso: toIsoDate(year, month, day) });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
};

const LANE_COUNT = 3;

// Which events keep their lane when a week runs out of room. A tradition sits
// above an activity because it comes round once a year and the activity comes
// round every week — losing Christmas to keep the gym would be the wrong trade.
const rank = (event) =>
    (event.tradition ? 2 : event.activity ? 1 : 0);

// Only holidays, traditions and what you scheduled reach the day lanes, so a day
// carries one or two marks at most and simple packing is enough.
const layoutWeek = (week, events) => {
    const isos = week.map((cell) => cell?.iso ?? null);
    const candidates = events
        .filter((e) => isos.some((iso) => iso && e.start <= iso && iso <= e.end))
        .sort((a, b) =>
            a.start.localeCompare(b.start) ||
            b.end.localeCompare(a.end) ||
            // a tradition, then something you scheduled, then whatever holiday
            // they happen to land on
            rank(b) - rank(a) ||
            a.title.localeCompare(b.title));

    const lanes = [];
    const segments = [];
    const overflow = Array(7).fill(0);
    candidates.forEach((event) => {
        const cols = isos
            .map((iso, c) => (iso && event.start <= iso && iso <= event.end ? c : null))
            .filter((c) => c !== null);
        const startCol = cols[0];
        const endCol = cols[cols.length - 1];
        let lane = lanes.findIndex(
            (taken) => !taken.some(([s, e]) => startCol <= e && endCol >= s));
        if (lane === -1) { lane = lanes.length; lanes.push([]); }
        if (lane >= LANE_COUNT) {
            for (let c = startCol; c <= endCol; c++) overflow[c] += 1;
            return;
        }
        lanes[lane].push([startCol, endCol]);
        segments.push({
            event,
            startCol,
            endCol,
            lane,
            continuesLeft: event.start < isos[startCol],
            continuesRight: event.end > isos[endCol],
        });
    });
    return { segments, overflow };
};

const emptyDraft = { start: '', end: '', title: '', time: '', notes: '' };

const emptyActivityDraft = {
    kind: 'ah',
    title: '',
    date: '',
    time: '',
    recurrence: 'once',
    until: '',
    notes: '',
    recipe: '',
};

export const CalendarPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const today = new Date();
    const [year, setYear] = useState(today.getFullYear());
    const [month, setMonth] = useState(today.getMonth());
    // Only holiday/custom events live in state; the watch plan is derived and
    // read-only, so it never has to be copied on every add or delete.
    const [events, setEvents] = useState(EVENTS);
    const [selectedDay, setSelectedDay] = useState(null);
    const [focusedTrack, setFocusedTrack] = useState(null);
    const [draft, setDraft] = useState(null);
    const [activityDraft, setActivityDraft] = useState(null);
    const [activitySaving, setActivitySaving] = useState(false);
    const [activityError, setActivityError] = useState(null);

    // "What should I watch?" — a written-out situation goes to the boards and
    // comes back as one film and one show. `tonight` is null when the dialog is
    // closed, so opening it is the same shape as the other dialogs here.
    const [tonight, setTonight] = useState(null);
    const [tonightPicks, setTonightPicks] = useState(null);
    const [tonightAsking, setTonightAsking] = useState(false);
    const [tonightError, setTonightError] = useState(null);

    // Dinners live in Mongo rather than in the bundle, so the rotation picks up
    // new or edited rows on the next load; failing just omits that track.
    // `activities` is the /bump pool — it stocks the activity dialog's picker
    // rather than a rotation. `activityEntries` are the scheduling rules you
    // saved, which is the only way anything gets onto a day now.
    const [dinners, setDinners] = useState([]);
    const [nalasMenu, setNalasMenu] = useState([]);
    const [activities, setActivities] = useState([]);
    const [activityEntries, setActivityEntries] = useState([]);
    const [movies, setMovies] = useState([]);
    const [restaurants, setRestaurants] = useState([]);
    const [shows, setShows] = useState([]);
    useEffect(() => {
        let live = true;
        getDinners()
            .then((rows) => { if (live && Array.isArray(rows)) setDinners(rows); })
            .catch(() => { /* calendar still works without dinners */ });
        getNalasMenu()
            .then((rows) => { if (live && Array.isArray(rows)) setNalasMenu(rows); })
            .catch(() => { /* calendar still works without the second dinner track */ });
        getActivities()
            .then((rows) => { if (live && Array.isArray(rows)) setActivities(rows); })
            .catch(() => { /* the dialog still takes a typed-in activity */ });
        getActivityEvents()
            .then((rows) => { if (live && Array.isArray(rows)) setActivityEntries(rows); })
            .catch(() => { /* calendar still works without scheduled activities */ });
        getMovies()
            .then((rows) => { if (live && Array.isArray(rows)) setMovies(rows); })
            .catch(() => { /* calendar still works without movies */ });
        getRestaurants()
            .then((rows) => { if (live && Array.isArray(rows)) setRestaurants(rows); })
            .catch(() => { /* calendar still works without restaurants */ });
        getShows()
            .then((rows) => { if (live && Array.isArray(rows)) setShows(rows); })
            .catch(() => { /* the generated plan stands on its own */ });
        return () => { live = false; };
    }, []);

    // darts added since the plan was generated, queued after it ends
    const newShowRuns = useMemo(() => buildNewShowRuns(shows), [shows]);

    // built first: the movie track pairs itself to whatever dinner is on
    const dinnerRuns = useMemo(
        () => buildDinnerRuns(dinners, PLAN_START, PLAN_END), [dinners]);

    const planRuns = useMemo(() => [
        ...SHOW_RUNS,
        ...dinnerRuns,
        ...buildNalasMenuRuns(nalasMenu, PLAN_START, PLAN_END),
        ...buildMovieRuns(movies, dinnerRuns, PLAN_START, PLAN_END),
        ...buildRestaurantRuns(restaurants, PLAN_START, PLAN_END),
        ...newShowRuns,
    ], [dinnerRuns, nalasMenu, movies, restaurants, newShowRuns]);

    const weeks = useMemo(() => buildWeeks(year, month), [year, month]);

    // The span the grid is showing, padding included.
    const gridRange = useMemo(() => {
        const cells = weeks.flat().filter(Boolean);
        if (!cells.length) return null;
        return { first: cells[0].iso, last: cells[cells.length - 1].iso };
    }, [weeks]);

    // A repeat is stored as one rule, so it only becomes days when a month asks
    // for them — a daily activity costs six weeks of events, not three years.
    const activityEvents = useMemo(
        () => (gridRange
            ? expandActivityEvents(activityEntries, gridRange.first, gridRange.last)
            : []),
        [activityEntries, gridRange]);

    // Traditions are rules too, resolved for the window on show rather than
    // built out year by year — the same deal the activity rules get.
    const traditionEvents = useMemo(
        () => (gridRange ? expandTraditions(gridRange.first, gridRange.last) : []),
        [gridRange]);

    const allEvents = useMemo(
        () => [...events, ...traditionEvents, ...activityEvents],
        [events, traditionEvents, activityEvents]);

    // Only the events touching the visible grid reach layoutWeek — otherwise
    // every week re-scans all ~1,100 plan nights.
    const visibleEvents = useMemo(() => {
        if (!gridRange) return [];
        return allEvents.filter(
            (e) => e.end >= gridRange.first && e.start <= gridRange.last);
    }, [allEvents, gridRange]);

    const changeMonth = (delta) => {
        const next = new Date(year, month + delta, 1);
        setYear(next.getFullYear());
        setMonth(next.getMonth());
    };

    const goToToday = () => {
        setYear(today.getFullYear());
        setMonth(today.getMonth());
    };

    const openCreate = (isoDate) =>
        setDraft({ ...emptyDraft, start: isoDate ?? toIsoDate(year, month, 1) });

    const openTonight = () => {
        setTonight('');
        setTonightPicks(null);
        setTonightError(null);
    };

    // The picks are deliberately not saved anywhere — this answers "tonight",
    // and the rotations already own what gets planned.
    const askTonight = async () => {
        const situation = (tonight ?? '').trim();
        if (!situation || tonightAsking) return;
        setTonightAsking(true);
        setTonightError(null);
        try {
            setTonightPicks(await recommendWatch(situation));
        } catch (error) {
            setTonightPicks(null);
            setTonightError(error.message || 'That did not come back.');
        } finally {
            setTonightAsking(false);
        }
    };

    const saveDraft = () => {
        if (!draft.title.trim() || !draft.start) return;
        const end = draft.end && draft.end > draft.start ? draft.end : draft.start;
        setEvents((prev) => [
            ...prev,
            {
                id: `custom-${Date.now()}`,
                title: draft.title.trim(),
                start: draft.start,
                end,
                time: draft.time || undefined,
                notes: draft.notes.trim() || undefined,
            },
        ]);
        setDraft(null);
    };

    const deleteEvent = (id) => setEvents((prev) => prev.filter((e) => e.id !== id));

    const openActivity = (isoDate, prefill) => {
        setActivityError(null);
        // No date given (the toolbar button) means today if it's on screen,
        // otherwise the first of the month being looked at.
        setActivityDraft({ ...emptyActivityDraft, date: isoDate ?? anchorIso, ...prefill });
    };

    // The picker is stocked from the /bump pool for the chosen kind, but the
    // title stays free text so a one-off doesn't have to be added to /bump first.
    const activityChoices = useMemo(() => {
        if (!activityDraft) return [];
        const symbol = ACTIVITY_KINDS[activityDraft.kind].symbol;
        return activities
            .filter((a) => a && a.header && a.symbol === symbol)
            .map((a) => a.header)
            .sort((a, b) => a.localeCompare(b));
    }, [activities, activityDraft]);

    // The bakes off Nala's menu — `fun` recipes don't rotate as dinners, so this
    // picker is where they live.
    const bakeChoices = useMemo(() => recipesOfType(nalasMenu, 'fun'), [nalasMenu]);
    const bakingSelected = isBakingActivity(activityDraft?.title);

    // Saving stays open until the write lands: a rule that only exists in this
    // tab would quietly disappear on reload, and the whole point is that these
    // are the days you chose.
    const saveActivity = async () => {
        const title = activityDraft.title.trim();
        if (!title || !activityDraft.date) return;
        const repeats = isRecurring(activityDraft.recurrence);
        const entry = {
            kind: activityDraft.kind,
            title,
            date: activityDraft.date,
            recurrence: activityDraft.recurrence,
            until: repeats && activityDraft.until > activityDraft.date
                ? activityDraft.until : '',
            time: activityDraft.time || '',
            notes: activityDraft.notes.trim(),
            // Only meaningful for a baking activity; picking a recipe and then
            // switching to another activity shouldn't smuggle it through.
            recipe: bakingSelected ? activityDraft.recipe : '',
        };
        setActivitySaving(true);
        setActivityError(null);
        try {
            const saved = await addActivityEvent(entry);
            setActivityEntries((prev) => [
                ...prev,
                saved && saved._id ? saved : { ...entry, _id: `local-${Date.now()}` },
            ]);
            setActivityDraft(null);
        } catch (error) {
            setActivityError("That didn't save — try again in a moment.");
        } finally {
            setActivitySaving(false);
        }
    };

    // A recurring activity is one rule, so removing an occurrence removes the
    // series — the button says so.
    const deleteActivityEntry = (entryId) => {
        setActivityEntries((prev) => prev.filter((e) => (e._id ?? e.id) !== entryId));
        deleteActivityEvent(entryId).catch(() => { /* already gone from the grid */ });
    };

    const monthLabel = new Date(year, month, 1).toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
    });

    const todayIso = toIsoDate(today.getFullYear(), today.getMonth(), today.getDate());

    // The strip reports on today when today is on screen, otherwise on the
    // first of whatever month is being viewed.
    const anchorIso = useMemo(() => {
        const first = toIsoDate(year, month, 1);
        const last = toIsoDate(year, month, new Date(year, month + 1, 0).getDate());
        return todayIso >= first && todayIso <= last ? todayIso : first;
    }, [year, month, todayIso]);

    // Arriving from /bump's Schedule button: that page hands over an activity,
    // and the dialog opens on it. The state is cleared straight away so going
    // back or refreshing doesn't pop the dialog again on a page you were only
    // trying to look at.
    useEffect(() => {
        const handoff = location.state?.scheduleActivity;
        if (!handoff) return;
        // An event from /bump also knows its day, time and place.
        openActivity(handoff.date ?? null, {
            title: handoff.header ?? '',
            kind: kindForSymbol(handoff.symbol) ?? emptyActivityDraft.kind,
            ...(handoff.time ? { time: handoff.time } : {}),
            ...(handoff.notes ? { notes: handoff.notes } : {}),
        });
        navigate(location.pathname, { replace: true });
        // openActivity is recreated every render; the handoff is what matters.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, navigate]);

    // Every track is mid-show on any given date — that is the whole slate.
    const inFlight = useMemo(() => slateFor(anchorIso, planRuns), [anchorIso, planRuns]);

    // Clicking a day opens the same slate for that date, plus whatever else it holds.
    const dayDetail = useMemo(() => {
        if (!selectedDay) return null;
        return {
            iso: selectedDay,
            slate: slateFor(selectedDay, planRuns),
            activities: allEvents.filter((e) => e.activity && e.start === selectedDay),
            // A tradition can be a span — the gratitude jar is open all November
            // — so it counts for every day it covers, not just the day it opens.
            traditions: allEvents.filter(
                (e) => e.tradition && e.start <= selectedDay && selectedDay <= e.end),
            others: allEvents.filter(
                (e) => !e.activity && !e.tradition
                    && e.start <= selectedDay && selectedDay <= e.end),
        };
    }, [selectedDay, allEvents, planRuns]);

    const daysInto = (run, iso) => {
        const from = new Date(`${run.show.start}T00:00:00`);
        const at = new Date(`${iso}T00:00:00`);
        const total = (new Date(`${run.show.end}T00:00:00`) - from) / 86400000 + 1;
        return { day: Math.round((at - from) / 86400000) + 1, total: Math.round(total) };
    };

    const yearOptions = useMemo(() => {
        const currentYear = Number(todayIso.slice(0, 4));
        const years = allEvents.map((e) => Number(e.start.slice(0, 4)));
        newShowRuns.forEach((r) => years.push(Number(r.show.end.slice(0, 4))));
        // A scheduled activity can sit outside the plan window, so its year has
        // to be reachable from the jump menu even when no month is showing it.
        activityEntries.forEach((e) => {
            if (e?.date) years.push(Number(e.date.slice(0, 4)));
        });
        const min = Math.min(currentYear, ...years);
        const max = Math.max(currentYear, ...years);
        return Array.from({ length: max - min + 1 }, (_, i) => min + i);
    }, [allEvents, todayIso, newShowRuns, activityEntries]);

    return (
        <div className="page">
            <Header />
            <div className="calendar-page">
                <div className="calendar">
                    <div className="calendar__toolbar">
                        <Button variant="outlined" onClick={() => changeMonth(-1)}>
                            ‹ Prev
                        </Button>
                        <h1 className="calendar__title">{monthLabel}</h1>
                        <Button variant="outlined" onClick={() => changeMonth(1)}>
                            Next ›
                        </Button>
                    </div>
                    <div className="calendar__toolbar calendar__toolbar--secondary">
                        <Button size="small" onClick={goToToday}>Today</Button>
                        <label className="calendar__jump">
                            <span className="calendar__sr-only">Month</span>
                            <select
                                value={month}
                                onChange={(e) => setMonth(Number(e.target.value))}
                            >
                                {MONTHS.map((name, i) => (
                                    <option key={name} value={i}>{name}</option>
                                ))}
                            </select>
                        </label>
                        <label className="calendar__jump">
                            <span className="calendar__sr-only">Year</span>
                            <select
                                value={year}
                                onChange={(e) => setYear(Number(e.target.value))}
                            >
                                {yearOptions.map((y) => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                        </label>
                        <Button size="small" variant="contained" onClick={() => openCreate()}>
                            + Add event
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => openActivity()}>
                            + Add activity
                        </Button>
                        <Button size="small" variant="outlined" onClick={openTonight}>
                            What should I watch?
                        </Button>
                    </div>

                    <div className="calendar__weekdays">
                        {WEEKDAYS.map((name) => (
                            <div key={name} className="calendar__weekday">{name}</div>
                        ))}
                    </div>
                    {weeks.map((week, w) => {
                        const { segments, overflow } = layoutWeek(week, visibleEvents);
                        const isos = week.map((c) => c?.iso ?? null);
                        // The focused track draws a continuous throughline across
                        // the days it covers — including tracks the grid normally
                        // stays silent about.
                        const throughlines = focusedTrack
                            ? planRuns
                                .filter((r) => r.code === focusedTrack)
                                .map((r) => {
                                    const cols = isos
                                        .map((iso, c) => (iso && r.show.start <= iso && iso <= r.show.end ? c : null))
                                        .filter((c) => c !== null);
                                    if (!cols.length) return null;
                                    return {
                                        id: r.id,
                                        title: r.show.title,
                                        startCol: cols[0],
                                        endCol: cols[cols.length - 1],
                                        opensLeft: r.show.start < isos[cols[0]],
                                        opensRight: r.show.end > isos[cols[cols.length - 1]],
                                    };
                                })
                                .filter(Boolean)
                            : [];
                        const weekShows = weekShowsFor(week, planRuns);
                        return (
                            <React.Fragment key={w}>
                            {weekShows.length > 0 && (
                                <div className="calendar__week-shows">
                                    {weekShows.map(({ code, run, first }) => {
                                        const { name, part } = splitTitle(run.show);
                                        return (
                                            <button
                                                key={code}
                                                type="button"
                                                className={`calendar__week-show calendar__week-show--${TRACKS[code].key}`
                                                    + (focusedTrack && focusedTrack !== code ? ' calendar__week-show--off' : '')}
                                                title={`${TRACKS[code].label}: ${run.show.title}`}
                                                onClick={() => setSelectedDay(
                                                    run.show.start > first ? run.show.start : first)}
                                            >
                                                <span className="calendar__track-icon" aria-hidden="true">
                                                    {TRACKS[code].icon}
                                                </span>
                                                <span className="calendar__week-show-name">{name}</span>
                                                {part && <span className="calendar__week-show-part">{part}</span>}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                            <div className={'calendar__week' + (focusedTrack ? ' calendar__week--focused' : '')}>
                                {week.map((cell, c) => {
                                    if (!cell) {
                                        return (
                                            <div
                                                key={`pad-${c}`}
                                                className="calendar__day calendar__day--empty"
                                                style={{ gridColumn: c + 1 }}
                                            />
                                        );
                                    }
                                    return (
                                        <div
                                            key={cell.iso}
                                            className={`calendar__day${cell.iso === todayIso ? ' calendar__day--today' : ''}`}
                                            style={{ gridColumn: c + 1 }}
                                        >
                                            {/* A blank day is not an empty day — every track is
                                                still mid-show, so the whole cell opens the slate. */}
                                            <button
                                                className="calendar__day-open"
                                                aria-label={`See what's on for ${cell.iso}`}
                                                onClick={() => setSelectedDay(cell.iso)}
                                            />
                                            <span className="calendar__day-top">
                                                <span className="calendar__day-number">{cell.day}</span>
                                                <button
                                                    className="calendar__add"
                                                    title="Add event"
                                                    aria-label={`Add event on ${cell.iso}`}
                                                    onClick={() => openCreate(cell.iso)}
                                                >
                                                    +
                                                </button>
                                            </span>
                                            {overflow[c] > 0 && (
                                                <span className="calendar__more">+{overflow[c]}</span>
                                            )}
                                        </div>
                                    );
                                })}
                                {throughlines.map((t) => (
                                    <span
                                        key={`thru-${t.id}`}
                                        className={
                                            `calendar__thru calendar__thru--${TRACKS[focusedTrack].key}`
                                            + (t.opensLeft ? ' calendar__thru--open-left' : '')
                                            + (t.opensRight ? ' calendar__thru--open-right' : '')
                                        }
                                        style={{ gridColumn: `${t.startCol + 1} / ${t.endCol + 2}`, gridRow: 2 }}
                                        title={t.title}
                                    >
                                        <span className="calendar__thru-label">{t.title}</span>
                                    </span>
                                ))}
                                {segments.map(({ event, startCol, endCol, lane, continuesLeft, continuesRight }) => {
                                    return (
                                    <button
                                        key={event.id}
                                        className={
                                            'calendar__event' +
                                            // holidays go recessive so colour stays meaningful;
                                            // events you add yourself keep the original blue
                                            (event.tradition
                                                ? ' calendar__event--tradition'
                                                : event.activity
                                                    ? ' calendar__event--activity'
                                                    : event.id.startsWith('custom-')
                                                        ? ''
                                                        : ' calendar__event--holiday') +
                                            (continuesLeft ? ' calendar__event--cont-left' : '') +
                                            (continuesRight ? ' calendar__event--cont-right' : '')
                                        }
                                        style={{
                                            gridColumn: `${startCol + 1} / ${endCol + 2}`,
                                            gridRow: lane + 2,
                                        }}
                                        title={event.tradition
                                            ? `${event.title} — ${event.tradition.note}`
                                            : event.activity
                                                ? `${event.activity.label}: ${event.title}`
                                                : event.title}
                                        onClick={() => setSelectedDay(event.start)}
                                    >
                                        {event.activity && (
                                            <span
                                                className="calendar__event-anchor"
                                                aria-hidden="true"
                                            >
                                                {event.activity.symbol}
                                            </span>
                                        )}
                                        {event.tradition && (
                                            <span
                                                className="calendar__event-anchor"
                                                aria-hidden="true"
                                            >
                                                {event.tradition.icon}
                                            </span>
                                        )}
                                        <span className="calendar__event-name">{event.title}</span>
                                    </button>
                                    );
                                })}
                            </div>
                            </React.Fragment>
                        );
                    })}

                    {inFlight.length > 0 && (
                        <div className="calendar__inflight">
                            <p className="calendar__inflight-title">
                                In rotation on {formatIsoDate(anchorIso)}
                                <span className="calendar__inflight-hint">
                                    all running at once — click any day to see its slate
                                </span>
                            </p>
                            {inFlight.map(({ code, run }) => {
                                const { day, total } = daysInto(run, anchorIso);
                                const on = focusedTrack === code;
                                return (
                                    <button
                                        key={code}
                                        type="button"
                                        aria-pressed={on}
                                        title={on ? 'Hide this track on the calendar'
                                                  : `Trace ${TRACKS[code].label} through the month`}
                                        onClick={() => setFocusedTrack(on ? null : code)}
                                        className={`calendar__inflight-row calendar__inflight-row--${TRACKS[code].key}`
                                            + (on ? ' calendar__inflight-row--on' : '')
                                            + (focusedTrack && !on ? ' calendar__inflight-row--off' : '')}
                                    >
                                        <span className={`calendar__track-dot calendar__track-dot--${TRACKS[code].key}`} />
                                        <span className="calendar__inflight-track">
                                            <span className="calendar__track-icon">{TRACKS[code].icon}</span>
                                            {TRACKS[code].label}
                                        </span>
                                        <span className="calendar__inflight-show">{run.show.title}</span>
                                        <span className="calendar__inflight-progress">
                                            <span
                                                className={`calendar__inflight-fill calendar__inflight-fill--${TRACKS[code].key}`}
                                                style={{ width: `${Math.min(100, (day / total) * 100)}%` }}
                                            />
                                        </span>
                                        <span className="calendar__inflight-count">
                                            {run.code === 'r'
                                                ? <>{run.show.poolSize}&nbsp;places</>
                                                : run.code === 'm'
                                                    ? <>{run.show.poolSize}&nbsp;films</>
                                                    : run.code === 'nm'
                                                        ? <>{run.show.poolSize}&nbsp;meals</>
                                                        : <>day&nbsp;{day}&nbsp;of&nbsp;{total}</>}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <Dialog open={draft !== null} onClose={() => setDraft(null)} fullWidth maxWidth="xs">
                <DialogTitle>New event</DialogTitle>
                <DialogContent className="calendar__dialog-content">
                    <TextField
                        label="Title"
                        value={draft?.title ?? ''}
                        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                        size="small"
                        autoFocus
                    />
                    <TextField
                        label="Start date"
                        type="date"
                        value={draft?.start ?? ''}
                        onChange={(e) => setDraft({ ...draft, start: e.target.value })}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="End date (optional)"
                        type="date"
                        value={draft?.end ?? ''}
                        onChange={(e) => setDraft({ ...draft, end: e.target.value })}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="Time (optional)"
                        type="time"
                        value={draft?.time ?? ''}
                        onChange={(e) => setDraft({ ...draft, time: e.target.value })}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="Notes (optional)"
                        value={draft?.notes ?? ''}
                        onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                        size="small"
                        multiline
                        minRows={2}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDraft(null)}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={!draft?.title.trim() || !draft?.start}
                        onClick={saveDraft}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Activities are scheduled, never assigned: nothing lands on a day
                unless it comes through here, once or on a repeat. */}
            <Dialog
                open={activityDraft !== null}
                onClose={() => (activitySaving ? null : setActivityDraft(null))}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Add an activity</DialogTitle>
                <DialogContent className="calendar__dialog-content">
                    <div className="calendar__kind-picker" role="group" aria-label="Kind">
                        {Object.entries(ACTIVITY_KINDS).map(([code, kind]) => (
                            <button
                                key={code}
                                type="button"
                                aria-pressed={activityDraft?.kind === code}
                                className={'calendar__kind'
                                    + (activityDraft?.kind === code ? ' calendar__kind--on' : '')}
                                onClick={() => setActivityDraft({ ...activityDraft, kind: code })}
                            >
                                <span className="calendar__kind-symbol">{kind.symbol}</span>
                                <span className="calendar__kind-label">{kind.label}</span>
                                <span className="calendar__kind-hint">{kind.hint}</span>
                            </button>
                        ))}
                    </div>

                    {activityChoices.length > 0 && (
                        <TextField
                            label="From your list"
                            select
                            value={activityChoices.includes(activityDraft?.title)
                                ? activityDraft.title : ''}
                            onChange={(e) =>
                                setActivityDraft({ ...activityDraft, title: e.target.value })}
                            size="small"
                            helperText="or type your own below"
                        >
                            {activityChoices.map((header) => (
                                <MenuItem key={header} value={header}>{header}</MenuItem>
                            ))}
                        </TextField>
                    )}

                    <TextField
                        label="Activity"
                        value={activityDraft?.title ?? ''}
                        onChange={(e) =>
                            setActivityDraft({ ...activityDraft, title: e.target.value })}
                        size="small"
                        autoFocus
                    />
                    {/* Baking is the one activity with something to pick inside
                        it: the `fun` recipes, which don't rotate as dinners. */}
                    {bakingSelected && bakeChoices.length > 0 && (
                        <TextField
                            label="Baking what?"
                            select
                            value={activityDraft?.recipe ?? ''}
                            onChange={(e) =>
                                setActivityDraft({ ...activityDraft, recipe: e.target.value })}
                            size="small"
                            helperText="optional — from the bakes on Nala's menu"
                        >
                            <MenuItem value="">
                                <span className="calendar__detail-muted">Decide later</span>
                            </MenuItem>
                            {bakeChoices.map((r) => (
                                <MenuItem key={r._id ?? r.name} value={r.name}>{r.name}</MenuItem>
                            ))}
                        </TextField>
                    )}

                    <TextField
                        label="Date"
                        type="date"
                        value={activityDraft?.date ?? ''}
                        onChange={(e) =>
                            setActivityDraft({ ...activityDraft, date: e.target.value })}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="Time (optional)"
                        type="time"
                        value={activityDraft?.time ?? ''}
                        onChange={(e) =>
                            setActivityDraft({ ...activityDraft, time: e.target.value })}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="Repeats"
                        select
                        value={activityDraft?.recurrence ?? 'once'}
                        onChange={(e) =>
                            setActivityDraft({ ...activityDraft, recurrence: e.target.value })}
                        size="small"
                        // Monthly follows the weekday, which isn't obvious from the
                        // word alone — say which weekday it worked out to be.
                        helperText={activityDraft?.recurrence === 'monthly' && activityDraft.date
                            ? `on ${monthlyPattern(activityDraft.date)} of each month`
                            : undefined}
                    >
                        {Object.entries(RECURRENCES).map(([code, { label }]) => (
                            <MenuItem key={code} value={code}>{label}</MenuItem>
                        ))}
                    </TextField>
                    {isRecurring(activityDraft?.recurrence) && (
                        <TextField
                            label="Until (optional)"
                            type="date"
                            value={activityDraft?.until ?? ''}
                            onChange={(e) =>
                                setActivityDraft({ ...activityDraft, until: e.target.value })}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            helperText="leave empty to keep repeating"
                        />
                    )}
                    <TextField
                        label="Notes (optional)"
                        value={activityDraft?.notes ?? ''}
                        onChange={(e) =>
                            setActivityDraft({ ...activityDraft, notes: e.target.value })}
                        size="small"
                        multiline
                        minRows={2}
                    />
                    {activityError && (
                        <p className="calendar__dialog-error">{activityError}</p>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button disabled={activitySaving} onClick={() => setActivityDraft(null)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={activitySaving
                            || !activityDraft?.title.trim()
                            || !activityDraft?.date}
                        onClick={saveActivity}
                    >
                        {activitySaving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Clicking any day shows every track at once — the grid only marks
                the switch-overs, so this is where "what's on" actually lives. */}
            <Dialog open={!!dayDetail} onClose={() => setSelectedDay(null)} fullWidth maxWidth="sm">
                {dayDetail && (
                    <>
                        <DialogTitle>{formatIsoDate(dayDetail.iso)}</DialogTitle>
                        <DialogContent className="calendar__dialog-content">
                            {/* Traditions lead the day: they're the thing the
                                day IS, where everything below is what's on. */}
                            {dayDetail.traditions.length > 0 && (
                                <div className="calendar__traditions">
                                    <p className="calendar__slate-heading">
                                        Traditions
                                        <span className="calendar__traditions-motto">
                                            Semper Dulce
                                        </span>
                                    </p>
                                    {dayDetail.traditions.map((e) => (
                                        <p key={e.id} className="calendar__detail">
                                            <span className="calendar__kind-symbol">
                                                {e.tradition.icon}
                                            </span>
                                            <strong>{e.title}</strong>
                                            {e.start !== e.end && (
                                                <span className="calendar__detail-muted">
                                                    {' · '}{formatShortDate(e.start)}
                                                    {' – '}{formatShortDate(e.end)}
                                                </span>
                                            )}
                                            {e.tradition.song && (
                                                <span className="calendar__detail-muted">
                                                    {' · ♫ '}{e.tradition.song}
                                                </span>
                                            )}
                                            {e.notes && (
                                                <span className="calendar__detail-muted">
                                                    {' — '}{e.notes}
                                                </span>
                                            )}
                                        </p>
                                    ))}
                                </div>
                            )}

                            {dayDetail.activities.length > 0 && (
                                <div className="calendar__activities">
                                    <p className="calendar__slate-heading">Activities</p>
                                    {dayDetail.activities.map((e) => {
                                        const repeat = describeRecurrence(e.activity);
                                        // A chosen bake is the only place a `fun`
                                        // recipe appears now that they're out of
                                        // the dinner rotation, so it brings the
                                        // whole recipe with it.
                                        const bake = e.activity.recipe
                                            && nalasMenu.find((m) => m.name === e.activity.recipe);
                                        return (
                                            // A div, not a p: a chosen bake brings an
                                            // ordered list with it, which a paragraph
                                            // can't legally hold.
                                            <div key={e.id} className="calendar__detail">
                                                <span className="calendar__kind-symbol">
                                                    {e.activity.symbol}
                                                </span>
                                                <strong>{e.title}</strong>
                                                {e.activity.recipe && (
                                                    <> · <strong>{e.activity.recipe}</strong></>
                                                )}
                                                {e.time && <> · {formatTime(e.time)}</>}
                                                <span className="calendar__detail-muted">
                                                    {' — '}{e.activity.label}
                                                    {repeat && `, ${repeat}`}
                                                </span>
                                                {e.notes && (
                                                    <span className="calendar__detail-muted">
                                                        {' · '}{e.notes}
                                                    </span>
                                                )}
                                                {bake && (
                                                    <div className="calendar__bake">
                                                        {bake.ingredients?.length > 0 && (
                                                            <span className="calendar__slate-meta">
                                                                <strong>Ingredients:</strong>{' '}
                                                                {bake.ingredients.join(' · ')}
                                                            </span>
                                                        )}
                                                        {bake.steps?.length > 0 && (
                                                            <ol className="calendar__steps">
                                                                {bake.steps.map((s, i) => (
                                                                    <li key={i}>{s}</li>
                                                                ))}
                                                            </ol>
                                                        )}
                                                    </div>
                                                )}
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    onClick={() =>
                                                        deleteActivityEntry(e.activity.entryId)}
                                                >
                                                    {repeat ? 'Delete series' : 'Delete'}
                                                </Button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {dayDetail.others.map((e) => (
                                <p key={e.id} className="calendar__detail">
                                    <strong>{e.title}</strong>
                                    {e.time && <> · {formatTime(e.time)}</>}
                                    {e.notes && (
                                        <span className="calendar__detail-muted"> — {e.notes}</span>
                                    )}
                                    {e.id.startsWith('custom-') && (
                                        <Button
                                            size="small"
                                            color="error"
                                            onClick={() => deleteEvent(e.id)}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                </p>
                            ))}

                            <p className="calendar__slate-heading">In rotation</p>
                            {dayDetail.slate.map(({ code, run }) => {
                                const { day, total } = daysInto(run, dayDetail.iso);
                                const isNew = run.show.start === dayDetail.iso;
                                return (
                                    <div key={code} className="calendar__slate-row">
                                        <span className={`calendar__slate-bar calendar__slate-bar--${TRACKS[code].key}`} />
                                        <span className="calendar__slate-body">
                                            <span className="calendar__slate-track">
                                                <span className="calendar__track-icon">{TRACKS[code].icon}</span>
                                                {TRACKS[code].label}
                                                <span className="calendar__detail-muted">
                                                    {' '}({TRACKS[code].types})
                                                </span>
                                            </span>
                                            <span className="calendar__slate-title">
                                                {run.show.title}
                                                {/* A nightly track doesn't "start" — it's just
                                                    what tonight offers. */}
                                                {isNew && run.show.end > run.show.start && (
                                                    <span className="calendar__slate-new">starts today</span>
                                                )}
                                                {run.show.holidayDate === dayDetail.iso && (
                                                    <span className="calendar__slate-new calendar__slate-new--holiday">
                                                        ★ {run.show.holiday} pick
                                                    </span>
                                                )}
                                            </span>
                                            {run.code === 'r' ? (
                                                <>
                                                    <span className="calendar__slate-meta">
                                                        {run.show.cuisine}
                                                        {run.show.neighborhood && ` · ${run.show.neighborhood}`}
                                                        {run.show.address && ` · ${run.show.address}`}
                                                        {` · 1 of ${run.show.poolSize} to try`}
                                                    </span>
                                                    {run.show.note && (
                                                        <span className="calendar__slate-meta">{run.show.note}</span>
                                                    )}
                                                </>
                                            ) : run.code === 'm' ? (
                                                <>
                                                    <span className="calendar__slate-meta">
                                                        {run.show.curated ? 'a curated pick' : 'from your Letterboxd export'}
                                                        {run.show.rating !== undefined && ` · ${run.show.rating}★`}
                                                        {run.show.heartlighted !== undefined
                                                            && ` · heartlighted ${run.show.heartlighted}`}
                                                        {run.show.liked && ' · liked'}
                                                        {run.show.watched ? ' · seen before' : ' · not seen yet'}
                                                        {` · 1 of ${run.show.poolSize} on this track`}
                                                    </span>
                                                    {run.show.pairedTo && (
                                                        <span className="calendar__slate-meta">
                                                            paired with tonight's {run.show.pairedTo} dinner
                                                        </span>
                                                    )}
                                                    {run.show.lists?.length > 0 && (
                                                        <span className="calendar__slate-meta">
                                                            on your list{run.show.lists.length > 1 ? 's' : ''}:{' '}
                                                            {run.show.lists.join(', ')}
                                                        </span>
                                                    )}
                                                    {run.show.blurb && (
                                                        <span className="calendar__slate-meta">{run.show.blurb}</span>
                                                    )}
                                                </>
                                            ) : run.code === 'nm' ? (
                                                <>
                                                    <span className="calendar__slate-meta">
                                                        {/* No recipe link to chase and nothing to
                                                            verify — the whole thing is right here,
                                                            unless it hasn't been written down yet.
                                                            A meal is still a decision about dinner
                                                            before it's a set of steps. */}
                                                        {run.show.steps.length === 0
                                                            ? 'recipe still to come'
                                                            : `one you wrote out · ${run.show.steps.length} `
                                                              + `step${run.show.steps.length === 1 ? '' : 's'}`}
                                                        {` · 1 of ${run.show.poolSize} on the menu`}
                                                    </span>
                                                    {/* The menu rotates store by store, so tonight's
                                                        meal also says which shopping trip it belongs
                                                        to and what else that trip is feeding. */}
                                                    <span className="calendar__slate-meta">
                                                        <strong>{run.show.store}</strong>
                                                        {run.show.storeSize > 1
                                                            && ` · night ${run.show.storeNight} of `
                                                               + `${run.show.storeSize} on this trip`}
                                                    </span>
                                                    {run.show.storeAhead?.length > 0 && (
                                                        <span className="calendar__slate-meta">
                                                            <strong>Still to come on this trip:</strong>{' '}
                                                            {run.show.storeAhead.slice(0, 3).join(' · ')}
                                                            {run.show.storeAhead.length > 3
                                                                && ` · +${run.show.storeAhead.length - 3} more`}
                                                        </span>
                                                    )}
                                                    {run.show.description && (
                                                        <span className="calendar__slate-meta">
                                                            {run.show.description}
                                                        </span>
                                                    )}
                                                    {run.show.ingredients.length > 0 && (
                                                        <span className="calendar__slate-meta">
                                                            <strong>Ingredients:</strong>{' '}
                                                            {run.show.ingredients.join(' · ')}
                                                        </span>
                                                    )}
                                                    {run.show.options.length > 0 && (
                                                        <span className="calendar__slate-meta">
                                                            <strong>Your pick:</strong>{' '}
                                                            {run.show.options.join(' · ')}
                                                        </span>
                                                    )}
                                                    {run.show.steps.length > 0 && (
                                                        <ol className="calendar__steps">
                                                            {run.show.steps.map((s, i) => (
                                                                <li key={i}>{s}</li>
                                                            ))}
                                                        </ol>
                                                    )}
                                                </>
                                            ) : run.code === 'n' ? (
                                                <>
                                                    <span className="calendar__slate-meta">
                                                        week {run.show.weekNumber} · {run.show.cuisine}
                                                        {run.show.occasion && ` · ${run.show.occasion}`}
                                                        {' · '}serves {run.show.servesNights} night
                                                        {run.show.servesNights === 1 ? '' : 's'}
                                                        {/* The week is divided between its meals, so a
                                                            meal can hold more nights than it serves.
                                                            Say both rather than let the dates imply
                                                            you're eating it four days running. */}
                                                        {run.show.coversNights !== run.show.servesNights
                                                            && `, on for ${run.show.coversNights}`}
                                                    </span>
                                                    {run.show.blurb && (
                                                        <span className="calendar__slate-meta">
                                                            {run.show.blurb}
                                                        </span>
                                                    )}
                                                    {run.show.supporting.length > 0 && (
                                                        <span className="calendar__slate-meta">
                                                            also this week:{' '}
                                                            {run.show.supporting
                                                                .map((s) => `${s.dinner} (${s.servesNights}n)`)
                                                                .join(' · ')}
                                                        </span>
                                                    )}
                                                    {(run.show.sides.length > 0 || run.show.dessert) && (
                                                        <span className="calendar__slate-meta">
                                                            {run.show.sides.join(', ')}
                                                            {run.show.sides.length > 0 && run.show.dessert && ' · '}
                                                            {run.show.dessert}
                                                        </span>
                                                    )}
                                                    {run.show.recipes.length > 0 && (
                                                        <span className="calendar__slate-meta">
                                                            {run.show.recipes.map((r) => (
                                                                <a
                                                                    key={r.url}
                                                                    className={
                                                                        'calendar__recipe'
                                                                        + (r.verified === false
                                                                            ? ' calendar__recipe--unverified' : '')
                                                                    }
                                                                    href={r.url}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    title={r.verified === false
                                                                        ? 'This link did not resolve when last checked'
                                                                        : r.url}
                                                                >
                                                                    {r.label}
                                                                    {r.verified === false && ' ⚠'}
                                                                </a>
                                                            ))}
                                                        </span>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <span className="calendar__slate-meta">
                                                        day {day} of {total} · about {run.show.nights} nights
                                                        of viewing · {run.show.rating}★ · heartlighted{' '}
                                                        {run.show.heartlighted} · enneagram {run.show.enneagram}
                                                    </span>
                                                    {run.show.addedLater && (
                                                        <span className="calendar__slate-meta">
                                                            added to the board after the plan was built, so it
                                                            is queued at the end of this track — length is a
                                                            placeholder, not a real runtime
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                            {(run.show.series || run.show.cut) && (
                                                <span className="calendar__slate-meta">
                                                    {run.show.series && (
                                                        <>block {run.show.part} of {run.show.parts}{' '}
                                                        for {run.show.series}</>
                                                    )}
                                                    {run.show.series && run.show.cut && ' · '}
                                                    {run.show.cut && <>watching {run.show.cut}</>}
                                                </span>
                                            )}
                                            <span className="calendar__slate-range">
                                                {run.show.start === run.show.end
                                                    ? formatIsoDate(run.show.start)
                                                    : <>{formatIsoDate(run.show.start)} – {formatIsoDate(run.show.end)}</>}
                                            </span>
                                        </span>
                                    </div>
                                );
                            })}
                            <p className="calendar__detail calendar__detail-muted">
                                All tracks run in parallel — watch whichever you feel like tonight.
                                Runtimes are estimates.
                            </p>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => openActivity(dayDetail.iso)}>Add activity</Button>
                            <Button onClick={() => openCreate(dayDetail.iso)}>Add event</Button>
                            <Button variant="contained" onClick={() => setSelectedDay(null)}>Close</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* A film and a show for tonight, read off the darts boards. */}
            <Dialog
                open={tonight !== null}
                onClose={() => setTonight(null)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>What should I watch?</DialogTitle>
                <DialogContent className="calendar__dialog-content">
                    <p className="calendar__detail calendar__detail-muted">
                        Say what kind of stretch you're in and you'll get one film and
                        one show back, both off the boards at /darts.
                    </p>
                    <TextField
                        label="Where you're at"
                        value={tonight ?? ''}
                        onChange={(e) => setTonight(e.target.value)}
                        multiline
                        minRows={3}
                        fullWidth
                        placeholder="Just bought a house and it's crazy having all this space to myself"
                    />
                    {tonightError && (
                        <p className="calendar__dialog-error">{tonightError}</p>
                    )}
                    {tonightPicks && (
                        <div className="calendar__tonight">
                            <p className="calendar__tonight-read">{tonightPicks.read}</p>
                            {[
                                { label: 'Film', pick: tonightPicks.movie },
                                { label: 'Show', pick: tonightPicks.show },
                            ].map(({ label, pick }) => pick && (
                                <div key={label} className="calendar__tonight-pick">
                                    <p className="calendar__slate-heading">{label}</p>
                                    <p className="calendar__tonight-title">{pick.title}</p>
                                    <p className="calendar__detail">{pick.why}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTonight(null)}>Close</Button>
                    <Button
                        variant="contained"
                        onClick={askTonight}
                        disabled={tonightAsking || !(tonight ?? '').trim()}
                    >
                        {tonightAsking ? 'Thinking…' : tonightPicks ? 'Ask again' : 'Ask'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};
